import { useEffect } from 'react';

import PassTypes from './PassTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PassConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  price?: number | null;
  originalPrice?: number | null;
  currency?: string;
  rawOptions?: unknown[];
  rawAddOns?: unknown[];
  activeCurrency?: string;
  isMultiOption?: boolean;
  selectedOptions?: string[];
  onOptionChange?: (opts: string[]) => void;
  selectedAddOns?: Record<string, boolean>;
  onSelectionChange?: (id: string, selected: boolean) => void;
  onAddToBasket?: (selection: { addOns: unknown[]; options: unknown[] }) => void | Promise<void>;
  buttonText?: string;
  disabled?: boolean;
  quantity?: number;
  onQuantityChange?: (qty: number) => void;
  hidePassDetails?: boolean;
  showOptions?: boolean;
  showAddOns?: boolean;
  buttonDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PassConfigurationModal = ({
  isOpen,
  onClose,
  title = '',
  description = '',
  price = null,
  originalPrice = null,
  currency = 'USD',
  rawOptions = [],
  rawAddOns = [],
  activeCurrency = 'USD',
  isMultiOption = false,
  selectedOptions = [],
  onOptionChange,
  selectedAddOns = {},
  onSelectionChange,
  onAddToBasket,
  buttonText = 'Update pass',
  quantity = 1,
  onQuantityChange,
  hidePassDetails = false,
  showOptions = true,
  showAddOns = true,
  disabled = false,
  buttonDisabled = false,
}: PassConfigurationModalProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatPrice = (amount: number | null | undefined, curr = currency): string => {
    if (amount === null || amount === undefined) return '';
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: curr,
    }).format(amount / 100);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-[460px] flex flex-col relative max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors p-1 z-20"
          aria-label="Close modal"
          type="button"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="overflow-y-auto flex-1 p-[32px]">
          <div>
            <PassTypes
              rawOptions={rawOptions as Parameters<typeof PassTypes>[0]['rawOptions']}
              rawAddOns={rawAddOns as Parameters<typeof PassTypes>[0]['rawAddOns']}
              activeCurrency={activeCurrency}
              isMultiOption={isMultiOption}
              selectedOptions={selectedOptions}
              onOptionChange={onOptionChange}
              selectedAddOns={selectedAddOns}
              onSelectionChange={onSelectionChange}
              onAddToBasket={onAddToBasket as Parameters<typeof PassTypes>[0]['onAddToBasket']}
              buttonText={buttonText}
              disabled={disabled}
              buttonDisabled={buttonDisabled}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              minimal={true}
              className="!p-0 !border-0 !shadow-none"
              formName="mobile-options"
              showPassConfigurationText={hidePassDetails}
              hidePassDetails={hidePassDetails}
              passName={title}
              passPrice={formatPrice(price)}
              passOriginalPrice={originalPrice ? formatPrice(originalPrice) : ''}
              passDateRange={description}
              showOptions={showOptions}
              showAddOns={showAddOns}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassConfigurationModal;
